import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styles from "./NeckSoundCheckNew.module.css";
import { useTranslation } from "react-i18next";
import { ArrowBack } from "@mui/icons-material";
import Step1Image from "./RecordingViewImage1.imageset/Group 54.svg";
import Step2Image from "./RecordingViewImage2.imageset/Group 24.svg";
import Step3Image from "./RecordingViewImage3.imageset/Group 55.svg";
import Step4Image from "./RecordingViewImage4.imageset/Group 30.svg";
import audioFile from "./Audio/01_earphone_test_en.wav";

const NeckSoundCheckNew: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hospitalId } = useParams();
  const prefix = hospitalId ? `/${hospitalId}` : '';
  const [currentStep, setCurrentStep] = useState(1);
  const [isBluetoothConnected, setIsBluetoothConnected] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [audioInputs, setAudioInputs] = useState<MediaDeviceInfo[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);

  useEffect(() => {
    const checkAudioDevices = async () => {
      try {
        // 미디어 장치 접근 권한 요청
        await navigator.mediaDevices.getUserMedia({ audio: true });

        // 오디오 장치 목록 가져오기
        const devices = await navigator.mediaDevices.enumerateDevices();
        console.log("Available audio devices:", devices);

        const audioOutputs = devices.filter(
          (device) => device.kind === "audiooutput"
        );
        console.log("Audio output devices:", audioOutputs);

        // 블루투스 이어폰이 연결되어 있는지 확인
        const hasBluetoothDevice = audioOutputs.some((device) => {
          const label = device.label.toLowerCase();
          console.log("Checking device:", label);
          return (
            label.includes("bluetooth") ||
            label.includes("airpods") ||
            label.includes("galaxy buds") ||
            label.includes("sony") ||
            label.includes("bose")
          );
        });

        console.log("Bluetooth device connected:", hasBluetoothDevice);
        setIsBluetoothConnected(hasBluetoothDevice);
      } catch (error) {
        console.error("오디오 장치 확인 중 오류:", error);
        setIsBluetoothConnected(false);
      }
    };

    // 초기 확인
    checkAudioDevices();

    // 주기적으로 상태 확인 (5초마다)
    const interval = setInterval(checkAudioDevices, 5000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // 오디오 요소 생성
    audioRef.current = new Audio(audioFile);

    return () => {
      // 컴포넌트 언마운트 시 오디오 정리
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    // 3단계로 넘어갈 때 음원 재생
    if (currentStep === 3 && audioRef.current) {
      console.log("Attempting to play audio...");
      console.log("Audio source:", audioRef.current.src);
      audioRef.current
        .play()
        .then(() => {
          console.log("Audio playback started successfully");
        })
        .catch((error) => {
          console.error("음원 재생 중 오류:", error);
        });
    }
  }, [currentStep]);

  useEffect(() => {
    if (currentStep === 5) {
      // 마이크 목록 가져오기
      navigator.mediaDevices
        .enumerateDevices()
        .then((devices) => {
          const inputs = devices.filter(
            (device) => device.kind === "audioinput"
          );
          setAudioInputs(inputs);
        })
        .catch((error) => {
          console.error("마이크 목록 가져오기 실패:", error);
        });
    }
  }, [currentStep]);

  useEffect(() => {
    let audioContext: AudioContext | null = null;
    let sourceNode: MediaStreamAudioSourceNode | null = null;

    if (currentStep === 5 && selectedDeviceId) {
      navigator.mediaDevices
        .getUserMedia({ audio: { deviceId: selectedDeviceId } })
        .then((stream) => {
          audioContext = new window.AudioContext();
          sourceNode = audioContext.createMediaStreamSource(stream);
          sourceNode.connect(audioContext.destination);
          console.log("선택된 마이크 소리가 이어폰으로 출력됩니다.");
        })
        .catch((error) => {
          console.error("마이크 스트림 시작 실패:", error);
        });
    }

    return () => {
      if (audioContext) {
        audioContext.close();
      }
    };
  }, [currentStep, selectedDeviceId]);

  const handleBack = () => {
    navigate(`${prefix}/necksound_check`);
  };

  const handleNext = () => {
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    }
  };

  const renderContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className={styles.content}>
            <h2 className={styles.title}>{t("necksound_step1_title")}</h2>
            <img src={Step1Image} alt="Step 1" className={styles.image} />
            <p className={styles.description}>
              {t("necksound_step1_description")}
            </p>
            <button onClick={handleNext} className={styles.nextButton}>
              {t("next_button_1")}
            </button>
          </div>
        );
      case 2:
        return (
          <div className={styles.content}>
            <h2 className={styles.title}>{t("necksound_step2_title")}</h2>
            <img src={Step2Image} alt="Step 2" className={styles.image} />
            <p className={styles.description}>
              {t("necksound_step2_description")}
            </p>
            <button
              onClick={handleNext}
              className={styles.nextButton}
              disabled={!isBluetoothConnected}
              style={{
                opacity: isBluetoothConnected ? 1 : 0.5,
                cursor: isBluetoothConnected ? "pointer" : "not-allowed",
              }}
            >
              {t("next_button_2")}
            </button>
          </div>
        );
      case 3:
        return (
          <div className={styles.content}>
            <h2 className={styles.title}>{t("necksound_step3_title")}</h2>
            <img src={Step3Image} alt="Step 3" className={styles.image} />
            <p className={styles.description}>
              {t("necksound_step3_description")}
            </p>
            <button onClick={handleNext} className={styles.nextButton}>
              {t("next_button_3")}
            </button>
          </div>
        );
      case 4:
        return (
          <div className={styles.content}>
            <h2 className={styles.title}>{t("necksound_step4_title")}</h2>
            <img src={Step4Image} alt="Step 4" className={styles.image} />
            <p className={styles.description}>
              {t("necksound_step3_description")}
            </p>
            <div className={styles.choiceButtonContainer}>
              <button onClick={handleNext} className={styles.choiceButton}>
                {t("next_button_4_right")}
              </button>
              <button onClick={handleNext} className={styles.choiceButton}>
                {t("next_button_4_left")}
              </button>
            </div>
          </div>
        );
      case 5:
        return (
          <div className={styles.content}>
            <h2 className={styles.title}>{t("necksound_step5_title")}</h2>
            <p className={styles.description}>
              {t("necksound_step5_description")}
            </p>

            <label htmlFor="microphoneSelect" className={styles.label}>
              {t("select_microphone_label")}
            </label>
            <select
              id="microphoneSelect"
              value={selectedDeviceId || ""}
              onChange={(e) => setSelectedDeviceId(e.target.value)}
              className={styles.select}
            >
              <option value="" disabled>
                {t("select_microphone_placeholder")}
              </option>
              {audioInputs.map((device) => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.label || `Microphone ${device.deviceId}`}
                </option>
              ))}
            </select>

            <button
              onClick={() => navigate(`${prefix}/necksound_check/result`)}
              className={styles.nextButton}
              disabled={!selectedDeviceId}
            >
              {t("next_button_5")}
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={handleBack}>
          <ArrowBack />
        </button>
        <span className={styles.headerText}>{t("check_before_1")}</span>
      </div>
      {renderContent()}
    </div>
  );
};

export default NeckSoundCheckNew;
